/* eslint-disable max-lines */
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { mapSeniorityType } from 'services/contacts/seniorityTypes';
import pluralize from 'pluralize';
import { reverseMapOrganizationHeadCount } from 'helpers/organizations';
import { mapStageToFund } from 'components/organisms/contactDetail/contactInfoWorkDate';
import { useSecurePdfOpener } from 'hooks/useSecurePdfOpener';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { contactsSortOptions } from 'components/organisms/contactsFilters/defaultFilters';

export const getContactHeader = (contactCount) =>
  `${contactCount?.toLocaleString()} ${pluralize('contact', contactCount ?? 0)}`;

export const useContactsTableRows = (data, { selectedRows, disabledContacts, ...options }) => {
  const history = useHistory();
  const { openSecurePdf, isLoading } = useSecurePdfOpener();
  const [selectedContacts, setSelectedContacts] = useState();

  const sortContactWorkExperiences = useCallback((contact) => {
    const experiences = [...contact.contactWorkExperiences];
    const primaryId = contact.primaryActivePosition?.id;
    const idx = experiences.findIndex((exp) => exp.id === primaryId);
    if (idx > 0) {
      const [primary] = experiences.splice(idx, 1);
      experiences.unshift(primary);
    }
    return experiences;
  }, []);

  return useMemo(
    () =>
      data?.map((contact, index) => {
        const sortedContactWorkExperiences = (sortContactWorkExperiences(contact) ?? []).filter((exp) => exp.active);
        const notes = contact?.notes?.length ?? 0;

        let contactUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}${history.location.search}`;
        let contactNotesUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}/notes${history.location.search}`;
        let jobApplicationsUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}/job-seeking${history.location.search}`;
        if (['list', 'list:add'].includes(options.page)) {
          contactUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}${history.location.search}`;
          contactNotesUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}/notes${history.location.search}`;
          jobApplicationsUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}/job-seeking${history.location.search}`;
        }

        if (options.type === 'public') {
          contactUrl = `/shared-lists/${options.list.slug}/${contact.id}${history.location.search}`;
          contactNotesUrl = `/shared-lists/${options.list.slug}/${contact.id}/notes${history.location.search}`;
          jobApplicationsUrl = `/shared-lists/${options.list.slug}/${contact.id}/job-seeking${history.location.search}`;
        }

        const row = {
          index,
          original: {
            ...contact,
            index,
          },
          options: {
            ...options,
            contactUrl,
            contactNotesUrl,
            jobApplicationsUrl,
            hideEdit: options.type === 'public',
          },
          selected: selectedRows.includes(index) || selectedRows.includes(contact.id),
          disabled: disabledContacts.includes(contact.id),
          rowId: contact.id,
          id: contact.id,
          name: contact.fullName,
          jobTitle: {
            experiences: sortedContactWorkExperiences,
            showLogo: false,
            columnKey: 'jobTitle',
          },
          company: {
            experiences: sortedContactWorkExperiences,
            showLogo: true,
            columnKey: 'companyName',
          },
          contactOf: contact?.importers ?? [],
          companySize: contact?.primaryActivePosition?.companySize
            ? reverseMapOrganizationHeadCount(contact?.primaryActivePosition?.companySize)
            : '',
          companyFunding: contact?.primaryActivePosition
            ? mapStageToFund(contact?.primaryActivePosition?.currentState)
            : '',
          linkedinProfile: contact.linkedinUrl ?? '',
          linkedinTagline: contact.linkedinTagline ?? '',
          workEmail: contact.workEmails ?? [],
          personalEmail: contact.personalEmails ?? [],
          location: contact.location?.name ?? '',
          seniority: mapSeniorityType(contact?.seniority),
          addedBy: contact.addedToSharedListBy?.user?.name ?? '',
          addedDate: contact.addedToSharedListBy?.createdAt
            ? moment(contact.addedToSharedListBy?.createdAt).format('MMM DD, YYYY')
            : '',
          list: contact?.sharedList ?? [],
          tags: contact?.tags ?? [],
          notes: notes > 0 ? `${notes} ${pluralize('note', notes)}` : '',
          talentNetworkSignup: contact?.signupInfo ? 'Yes' : 'No',
          signupDate: contact?.signupInfo?.signupDate
            ? moment(contact?.signupInfo?.signupDate).format('MMM DD, YYYY')
            : '',
          firstSeen: contact?.addedAt ? moment(contact?.addedAt).format('MMM DD, YYYY') : '',
          preferredJobFunction: contact?.signupInfo?.jobFunctions?.join(', ') ?? '',
          levelOfExperience: contact?.signupInfo?.seniorities?.join(', ') ?? '',
          preferredLocation: contact?.signupInfo?.locationPreferences?.join(', ') ?? '',
          workEnvironment: contact?.signupInfo?.workEnvironments?.join(', ') ?? '',
          jobSearchStatus: contact?.signupInfo?.jobSearchStatus ?? '',
          resume: {
            url: contact?.signupInfo?.resumeUrl ?? '',
            getResume: () => {
              openSecurePdf(contact.id);
              setSelectedContacts(contact.id);
            },
            isLoading: selectedContacts === contact.id && isLoading,
          },
          jobApplications: contact?.jobApplicationsCount ?? 0,
          jobAlerts: contact?.signupInfo?.hasJobAlerts ? 'Yes' : 'No',
          jobAlertsKeywords: contact?.signupInfo?.jobAlertKeywords ?? '',
          website: contact?.signupInfo?.websiteUrl ? addProtocolToUrl(contact.websiteUrl) : '',
          industries: contact?.signupInfo?.industries?.join(', ') ?? '',
          yearsOfExperience: contact?.yearsOfExperience ? `${Math.ceil(contact.yearsOfExperience)}` : '',
          yearsInCurrentCompany: Math.ceil(contact?.primaryActivePosition?.duration ?? 0),
          industry:
            contact?.primaryActivePosition?.industryTags
              ?.map((tag) => tag.description)
              .filter((tag) => tag)
              .join(', ') ?? '',
          source: contact?.sources ?? [],
          customFields: contact?.customFields ?? [],
        };

        return row;
      }) ?? [],
    [
      data,
      sortContactWorkExperiences,
      options,
      history.location.search,
      selectedRows,
      disabledContacts,
      selectedContacts,
      isLoading,
      openSecurePdf,
    ],
  );
};

export const useSortableColumns = ({ queryParams, columns }) => {
  const items = useMemo(() => {
    const sortableColumns = new Set([
      'name',
      'jobTitle',
      'location',
      'seniority',
      'company',
      'contactOf',
      'companyFunding',
      'companySize',
      'yearsOfExperience',
      'yearsInCurrentCompany',
      'signupDate',
      'firstSeen',
    ]);
    const result = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const col of sortableColumns) {
      const column = columns.find((c) => c.accessor === col);
      if (column) {
        result.push({
          accessor: column.accessor,
          Header: column.Header,
        });
      }
    }

    return result;
  }, [columns]);

  const initialSortBy = useMemo(() => {
    const { sortBy } = queryParams;
    const sortKey = contactsSortOptions.find((option) => option.value === sortBy);
    if (sortKey) {
      return {
        id: sortKey.field,
        desc: queryParams.sortDirection === 'desc',
      };
    }
    return {
      id: 'relevance',
      desc: true,
    };
  }, [queryParams]);

  return {
    sortableColumns: items,
    initialSortBy,
  };
};
